<script setup>
/**
 * @fileoverview Step 2 — pick a glaze, then paint tiles in the preview.
 *
 * Also hosts paint-by-size and the parts list (collapsed until clicked).
 *
 * @component
 */
import { ref } from 'vue';
import { useMosaicStore } from '../stores/mosaicStore';
import { formatSize } from '../utils/patternData.js';
import { trackPaint } from '../utils/trackingService';
import GlazePicker from './GlazePicker.vue';
import PartsList from './PartsList.vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';

const store = useMosaicStore();

const partsOpen = ref(false);

function paintSize(size) {
    const changed = store.paintSize(size);
    if (changed) {
        trackPaint(store.selectedPatternId, 'size', changed, store.activeColor?.name);
    }
}
</script>

<template>
    <div class="p-4 flex flex-col gap-5">
        <section aria-labelledby="cm-paint-heading" class="flex flex-col gap-2">
            <h2 id="cm-paint-heading" class="text-sm font-medium text-dryline mb-0">
                Choose a color, then click tiles in the preview
            </h2>
            <GlazePicker />
        </section>

        <section
            v-if="store.selectedPattern"
            aria-labelledby="cm-size-heading"
            class="flex flex-col gap-2"
        >
            <h2 id="cm-size-heading" class="text-sm font-medium text-dryline mb-0">
                Paint all tiles of a size
            </h2>
            <div class="flex flex-wrap gap-2">
                <button
                    v-for="size in store.selectedPattern.sizes"
                    :key="size"
                    type="button"
                    class="px-3 py-1.5 border border-dryline text-xs text-dryline bg-white hover:bg-dryline hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-dryline"
                    :disabled="!store.activeColor"
                    @click="paintSize(size)"
                >
                    All {{ formatSize(size) }} ({{ store.selectedPattern.tileCounts[size] }})
                </button>
            </div>
            <p v-if="!store.activeColor" class="text-[11px] text-dryline mb-0">
                Select a color above to enable.
            </p>
        </section>

        <section class="flex flex-col gap-2">
            <h2 class="mb-0">
                <button
                    type="button"
                    class="w-full flex items-center justify-between gap-2 text-sm font-medium text-dryline hover:text-black"
                    :aria-expanded="partsOpen ? 'true' : 'false'"
                    aria-controls="cm-parts-panel"
                    data-testid="parts-toggle"
                    @click="partsOpen = !partsOpen"
                >
                    <span>
                        Parts List
                        <span class="font-normal">({{ partsOpen ? 'hide' : 'show' }})</span>
                    </span>
                    <ChevronDownIcon
                        class="w-4 h-4 transition-transform"
                        :class="partsOpen ? 'rotate-180' : ''"
                        aria-hidden="true"
                    />
                </button>
            </h2>
            <div v-show="partsOpen" id="cm-parts-panel">
                <PartsList />
            </div>
        </section>
    </div>
</template>
